import { useState } from 'react';
import type { FormEvent } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Login, useLogin, useNotify } from 'react-admin';

export function LoginPage() {
    const login = useLogin();
    const notify = useNotify();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitting(true);
        setErrorMessage(null);

        try {
            await login({ email, password });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Login failed';
            setErrorMessage(message);
            notify(message, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Login>
            <Card sx={{ width: '100%', maxWidth: 460, borderRadius: 4, boxShadow: '0 24px 80px rgba(15, 23, 42, 0.18)' }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack spacing={3} component="form" onSubmit={handleSubmit}>
                        <Box>
                            <Typography variant="h4" fontWeight={800} gutterBottom>
                                Admin Login
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Sign in to access blog content, exam task review queues, and admin settings.
                            </Typography>
                        </Box>

                        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                        <TextField
                            label="Email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="email"
                            autoFocus
                            fullWidth
                            required
                        />

                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="current-password"
                            fullWidth
                            required
                        />

                        <Button type="submit" variant="contained" size="large" disabled={submitting}>
                            {submitting ? 'Signing in...' : 'Sign in'}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Login>
    );
}